import React, { forwardRef, useEffect, useState } from 'react';
import AsyncSelect from 'react-select/async';
import { components } from 'react-select';
import { ComponentLoader, Button, FormGroup, Select, Row, Col } from '@snsw/react-component-library';
import { OrganisationSearchAutoCompleteService } from './OrganisationSearchAutoCompleteService';
import FieldError from '../fieldError';
import styles from './OrganisationSearchAutoComplete.module.scss';
import { isEmpty } from 'shared/utils/Validation';

const commonState = {
  borderWidth: styles.inputBorderWidth,
  height: styles.inputHeight,
  borderRadius: styles.inputBorderRadius,
  marginTop: '0.25rem',
};

const normalState = {
  control: (provided) => ({
    ...provided,
    ...commonState,
    borderColor: styles.colorGreyShuttle,
  }),
};

const errorState = {
  control: (provided) => ({
    ...provided,
    ...commonState,
    borderColor: styles.colorPrimaryRed,
  }),
};

let timer;

function debounce(func, timeout = 300) {
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, timeout);
  };
}

export const OrganisationSearchAutoComplete = forwardRef(
  (
    {
      id,
      name,
      hasError,
      errorMessage,
      title = '',
      businessNamesTitle = 'Would you like to add a registered business name to this registration? (optional)',
      businessNamePlaceholder = 'Please select',
      placeholder = '',
      helpMessage = '',
      className = '',
      selectedOrganisation = {},
      selectedBusinessName = '',
      onChangeOrganisation = () => {},
      onChangeBusinessName = () => {},
      onClear = () => {},
      apiRoute = '/abnAcnApi/lookup',
      baseURL = '',
      maxResult = 10,
    },
    ref,
  ) => {
    const [fetchingDetail, setFetchingDetail] = useState(false);
    const [detail, setDetail] = useState();
    const [businessName, setBusinessName] = useState(selectedBusinessName);
    const [businessNamesList, setBusinessNamesList] = useState([]);

    useEffect(() => {
      if (selectedOrganisation) getAbnAcnDetail(selectedOrganisation);
    }, []);

    useEffect(() => {
      if (detail && detail.businessNames && detail.businessNames.length > 0) {
        const newList = detail.businessNames.sort().map((name) => {
          let option = {};
          option['text'] = name;
          option['value'] = name;
          return option;
        });
        setBusinessNamesList(newList);
      } else {
        setBusinessNamesList([]);
      }
    }, [detail]);

    const clearResult = () => {
      setFetchingDetail(false);
      setDetail(undefined);
      onChangeOrganisation(undefined);
    };

    const getAbnAcnDetail = (searchTerm) => {
      if (!isEmpty(searchTerm.abn) || !isEmpty(searchTerm.acn)) {
        //if response is from abn or acn search, no need to call api again
        //the response will have businessNames property
        if (searchTerm.businessNames !== undefined) {
          setFetchingDetail(false);
          setDetail(searchTerm);
          onChangeOrganisation(searchTerm);
        } else {
          setFetchingDetail(true);
          if (!isEmpty(searchTerm.abn)) {
            OrganisationSearchAutoCompleteService.getDetailByAbn(`${baseURL}${apiRoute}`, searchTerm.abn).then(
              (detail) => {
                setFetchingDetail(false);
                setDetail(detail[0]);
                onChangeOrganisation(detail[0]);
              },
            );
          } else if (!isEmpty(searchTerm.acn)) {
            OrganisationSearchAutoCompleteService.getDetailByAcn(`${baseURL}${apiRoute}`, searchTerm.acn).then(
              (detail) => {
                setFetchingDetail(false);
                setDetail(detail[0]);
                onChangeOrganisation(detail[0]);
              },
            );
          }
        }
      }
    };

    return (
      <div className={`${className} mainAbnAcncontainer`} id={id}>
        {title && <div className={styles.title}>{title}</div>}
        {!fetchingDetail && detail ? (
          <>
            <div className={styles.detail}>
              <div className={styles.titleLine}>
                <h3 className={styles.titleLeft}>{detail.name}</h3>
                <div className={styles.titleRight}>
                  <Button
                    variant={'link'}
                    onClick={(e) => {
                      e.preventDefault();
                      clearResult(true);
                      onClear();
                    }}
                  >
                    Clear
                  </Button>
                </div>
              </div>
              {detail.abn ? (
                <div className={styles.detailLine}>
                  <div className={styles.detailLeft}>ABN</div>
                  <div className={styles.detailRight}>{detail.abn}</div>
                </div>
              ) : null}
              {detail.acn ? (
                <div className={styles.detailLine}>
                  <div className={styles.detailLeft}>
                    {detail.entityType === 'Other Incorporated Entity' ? 'ARBN' : 'ACN'}
                  </div>
                  <div className={styles.detailRight}>{detail.acn}</div>
                </div>
              ) : null}
            </div>
            {businessNamesList && businessNamesList.length > 0 ? (
              <>
                <FormGroup label={businessNamesTitle} id={`businesName-${id}`}>
                  <Select
                    options={businessNamesList}
                    onChange={(e) => {
                      setBusinessName(e.target.value);
                      onChangeBusinessName(e.target.value);
                    }}
                    value={businessName}
                    placeholder={businessNamePlaceholder}
                    inputWidth="xl"
                  />
                </FormGroup>
              </>
            ) : null}
          </>
        ) : (
          <>
            {helpMessage && <div className={styles.helpMsg}>{helpMessage}</div>}
            <AsyncSelect
              hasError={hasError}
              id={`asyncSelect1-${id}`}
              name={`asyncSelect1-${name}`}
              defaultOptions
              cacheOptions
              styles={hasError ? errorState : normalState}
              components={{
                DropdownIndicator: () => null,
                IndicatorSeparator: () => null,
                SingleValue: ({ children, data, ...props }) => (
                  <components.SingleValue {...props}>{data.label}</components.SingleValue>
                ),
              }}
              loadOptions={(inputValue) =>
                new Promise((res) =>
                  debounce(
                    () =>
                      res(
                        OrganisationSearchAutoCompleteService.searchByAbnAcnBusinessName(
                          `${baseURL}${apiRoute}`,
                          inputValue,
                          maxResult,
                        ),
                      ),
                    1000,
                  )(),
                )
              }
              getOptionLabel={(e) => e?.name}
              getOptionValue={(e) => e?.abn}
              onChange={(v) => {
                if (v.abn || v.acn) {
                  getAbnAcnDetail(v);
                }
              }}
              placeholder={placeholder}
            />

            {hasError && errorMessage ? <FieldError message={errorMessage} /> : null}
            {fetchingDetail ? (
              <Row>
                <Col span={12}>
                  <ComponentLoader fullPage />
                </Col>
              </Row>
            ) : null}
          </>
        )}
      </div>
    );
  },
);

OrganisationSearchAutoComplete.displayName = 'OrganisationSearchAutoComplete';
